import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Code2 } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Portfolio() {
  const [portfolio, setPortfolio] = useState([]);
  const [filter, setFilter] = useState('All');
  const [loading, setLoading] = useState(true);

  const categories = ['All', 'Web', 'IoT', 'PCB', 'DevOps', 'Mentoring'];

  // Dummy data for initial display
  const dummyPortfolio = [
    {
      id: '1',
      title: 'E-Commerce Dashboard',
      category: 'Web',
      description: 'Full-stack dashboard untuk manajemen toko online dengan real-time analytics dan inventory management.',
      tech_stack: ['React', 'Node.js', 'PostgreSQL', 'Docker'],
      image_url: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=400&fit=crop',
      impact: 'Meningkatkan efisiensi operasional 40%'
    },
    {
      id: '2',
      title: 'Smart Home IoT System',
      category: 'IoT',
      description: 'Sistem monitoring dan kontrol perangkat rumah menggunakan ESP32 dengan web dashboard real-time.',
      tech_stack: ['ESP32', 'MQTT', 'React', 'WebSocket'],
      image_url: 'https://images.unsplash.com/photo-1558002038-1055907df827?w=600&h=400&fit=crop',
      impact: 'Monitoring 10+ sensor secara real-time'
    },
    {
      id: '3',
      title: 'Greenhouse Monitoring System',
      category: 'IoT',
      description: 'Sistem monitoring suhu, kelembapan, dan cahaya untuk greenhouse dengan automated control.',
      tech_stack: ['ESP8266', 'DHT22', 'Node-RED', 'InfluxDB'],
      image_url: 'https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?w=600&h=400&fit=crop',
      impact: 'Optimasi kondisi tanaman 30%'
    },
    {
      id: '4',
      title: 'Custom PCB for Data Logger',
      category: 'PCB',
      description: 'Desain PCB custom untuk data logger dengan multiple sensor input dan SD card storage.',
      tech_stack: ['KiCAD', 'STM32', 'Sensor Integration'],
      image_url: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop',
      impact: 'Reduce size 50% dari prototipe breadboard'
    },
    {
      id: '5',
      title: 'Microservices Deployment',
      category: 'DevOps',
      description: 'Setup dan deployment microservices architecture dengan Docker, Kubernetes, dan monitoring stack.',
      tech_stack: ['Docker', 'Kubernetes', 'AWS', 'Grafana'],
      image_url: 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=600&h=400&fit=crop',
      impact: 'Zero-downtime deployment'
    },
    {
      id: '6',
      title: 'Final Year Project Mentoring',
      category: 'Mentoring',
      description: 'Bimbingan proyek akhir sistem monitoring industri dengan IoT dan web dashboard untuk mahasiswa teknik.',
      tech_stack: ['ESP32', 'React', 'Firebase', 'Mentoring'],
      image_url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop',
      impact: 'Project berhasil A dengan publikasi'
    }
  ];

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API}/portfolio`);
      if (response.data && response.data.length > 0) {
        setPortfolio(response.data);
      } else {
        setPortfolio(dummyPortfolio);
      }
    } catch (error) {
      console.error('Error fetching portfolio:', error);
      setPortfolio(dummyPortfolio);
    } finally {
      setLoading(false);
    }
  };

  const filteredPortfolio = filter === 'All' 
    ? portfolio 
    : portfolio.filter(item => item.category === filter);

  return (
    <div className="min-h-screen pt-20" data-testid="portfolio-page">
      {/* Hero */}
      <section className="pt-32 pb-20 px-6 noise-overlay">
        <div className="container mx-auto text-center">
          <div className="inline-block px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full text-cyan-400 text-sm font-medium mb-4">
            Project Showcase
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="portfolio-title">
            Portfolio &
            <br />
            <span className="gradient-text">Project Highlights</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto">
            Koleksi project yang telah diselesaikan, dari web development, IoT, hingga engineering support.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="pb-12 px-6">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                onClick={() => setFilter(category)}
                variant={filter === category ? 'default' : 'outline'}
                className={filter === category 
                  ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                  : 'btn-secondary'
                }
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="pb-20 px-6">
        <div className="container mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-slate-400 mt-4">Loading portfolio...</p>
            </div>
          ) : filteredPortfolio.length === 0 ? (
            <div className="text-center py-20">
              <Code2 size={64} className="text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">Belum ada project di kategori ini.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPortfolio.map((item, index) => (
                <Card key={item.id || index} className="glass-effect border-slate-700 overflow-hidden card-hover group" data-testid={`portfolio-item-${index}`}>
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden bg-slate-800">
                    <img 
                      src={item.image_url || 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600&h=400&fit=crop'} 
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute top-3 right-3">
                      <span className="px-3 py-1 bg-cyan-500/90 text-white text-xs font-medium rounded-full">
                        {item.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-slate-400 text-sm mb-4 line-clamp-2">
                      {item.description}
                    </p>

                    {/* Tech Stack */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tech_stack.slice(0, 4).map((tech, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-slate-800 text-slate-300 rounded">
                          {tech}
                        </span>
                      ))}
                    </div>

                    {/* Impact */}
                    {item.impact && (
                      <div className="pt-4 border-t border-slate-700">
                        <p className="text-xs text-cyan-400 font-medium flex items-center">
                          <ExternalLink size={14} className="mr-1" />
                          {item.impact}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-slate-900/50">
        <div className="container mx-auto">
          <Card className="glass-effect border-slate-700 p-6 sm:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Mau Bangun Project Serupa?
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto mb-8">
              Ceritakan ide project Anda. Kami bantu wujudkan dari konsep hingga deployment.
            </p>
            <div className="flex justify-center">
              <Link to="/contact" className="w-full sm:w-auto">
                <Button className="btn-primary text-base px-8 py-6 w-full sm:w-auto" data-testid="portfolio-cta">
                  Diskusi Project
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}